import { useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import {
  useLocation,
  useNavigate,
  type NavigateFunction,
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import { APP_TO_BUILD } from "../../appsConfig";
import {
  redeemOrgAccessCode,
  type RedeemOrgAccessCodeResult,
} from "@/api/orgPurchases";
import { useRootStore, type RootStore } from "@/state/RootStore";
import { captureEvent } from "@/utils/posthog";
import { getOrgRedeemSuccessRoute } from "@/utils/orgPurchases/redemptionRouting";
import {
  clearPendingAccessCode,
  getPendingAccessCodeIdentity,
  readPendingAccessCode,
  type PendingAccessCode,
} from "@/utils/orgPurchases/pendingAccessCode";
import {
  getAutoRedemptionRetryDelay,
  hasFullStoredAccessForOrgRedemption,
  isAmbiguousRedemptionRecoveryCandidate,
  type AutoRedeemErrorReason,
} from "@/utils/orgPurchases/redemptionPolicy";
import { finalizeOrgRedemption } from "@/utils/orgPurchases/redemptionCompletion";
import { isRequestTimeoutError } from "@/lib/requestTimeout";

const STORE_READY_TIMEOUT_MS = 30_000;

export type PendingAccessCodePresentation =
  | { status: "idle" }
  | { status: "redeeming"; pending: PendingAccessCode }
  | { status: "success"; pending: PendingAccessCode }
  | {
      status: "error";
      pending: PendingAccessCode;
      reason: AutoRedeemErrorReason;
    };

type ConsumePendingAccessCodeResult =
  | "no-pending"
  | "session-changed"
  | "success"
  | "typed-failure"
  | "transient-failure";

interface ConsumePendingAccessCodeOpts {
  store: RootStore;
  navigate: NavigateFunction;
  isMobileWeb: boolean;
  isSessionCurrent?: () => boolean;
  /**
   * True once the student has dismissed the redeeming dialog.
   *
   * Dismissing does NOT cancel the run — retries continue and can still succeed. What it
   * must stop is the flow SEIZING THE SCREEN afterwards: without this, a dismissed
   * redemption could pop the error modal open a minute later on whatever page they had
   * moved to, or `flushSync`-navigate them away from it on success.
   */
  isAbandoned?: () => boolean;
  onPresentationChange?: (presentation: PendingAccessCodePresentation) => void;
}

let inFlight: Promise<ConsumePendingAccessCodeResult> | null = null;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function waitForStoreReady(store: RootStore) {
  const startedAt = Date.now();

  while (Date.now() - startedAt < STORE_READY_TIMEOUT_MS) {
    if (
      store.mainAppStore.storeInitialized &&
      store.mainAppStore.profileSaved
    ) {
      return true;
    }
    await delay(100);
  }

  return false;
}

function mapLegacyResult(result: string): AutoRedeemErrorReason {
  switch (result) {
    case "NOT_FOUND":
      return "not_found";
    case "ALREADY_REDEEMED":
      return "already_redeemed";
    default:
      return "unknown";
  }
}

async function redeemStoredAccessCode(
  pending: PendingAccessCode,
  store: RootStore,
): Promise<{ ok: true } | { ok: false; reason: AutoRedeemErrorReason }> {
  const blockedReason = store.mainAppStore.getOrgRedeemBlockedReason();
  if (blockedReason) {
    return { ok: false, reason: blockedReason };
  }

  const result: RedeemOrgAccessCodeResult = await redeemOrgAccessCode({
    code: pending.code,
    examType: pending.examType,
  });

  if ("reason" in result) {
    if (result.reason === "not_found" && APP_TO_BUILD === "electrical_us") {
      const legacyResult = await store.mainAppStore.redeemCode(pending.code);
      if (legacyResult === "Success") {
        return { ok: true };
      }

      return { ok: false, reason: mapLegacyResult(legacyResult) };
    }

    return { ok: false, reason: result.reason };
  }

  return { ok: true };
}

async function confirmCurrentUserReceivedOrgAccess(store: RootStore) {
  try {
    await store.mainAppStore.checkPurchaseState();
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        area: "org_access_code",
        phase: "verify_after_ambiguous_redeem",
      },
    });
    return false;
  }

  return hasFullStoredAccessForOrgRedemption(
    APP_TO_BUILD,
    store.mainAppStore.newPurchaseState,
  );
}

async function consumePendingAccessCodeInner({
  store,
  navigate,
  isMobileWeb,
  isSessionCurrent = () => true,
  isAbandoned = () => false,
  onPresentationChange,
}: ConsumePendingAccessCodeOpts): Promise<ConsumePendingAccessCodeResult> {
  const pending = readPendingAccessCode();
  if (!pending) return "no-pending";

  // Snapshot the viewport classification for this attempt so resizing while
  // the request is in flight cannot change its analytics or success route.
  const platform = isMobileWeb ? "mobile_web" : "web";
  onPresentationChange?.({ status: "redeeming", pending });
  const storeReady = await waitForStoreReady(store);

  if (!isSessionCurrent()) {
    onPresentationChange?.({ status: "idle" });
    return "session-changed";
  }

  if (storeReady === false) {
    const error = new Error(
      "Timed out waiting for the app store to initialize.",
    );
    Sentry.captureException(error, {
      tags: { area: "org_access_code", phase: "store_ready" },
    });
    onPresentationChange?.({ status: "error", pending, reason: "unknown" });
    return "transient-failure";
  }

  // Make the loading state observable after store initialization as well as
  // when the pending record is first discovered.
  onPresentationChange?.({ status: "redeeming", pending });

  const completeSuccessfulRedemption = async () => {
    clearPendingAccessCode(pending);
    captureEvent("org_access_code_auto_redeem_success", {
      exam_type: pending.examType,
      platform,
    });

    const successRoute = getOrgRedeemSuccessRoute(
      store.mainAppStore.isProfileComplete,
      { isMobileWeb },
    );
    const completion = await finalizeOrgRedemption({
      successRoute,
      navigate: (route) => {
        // Entitlement is still finalised and purchase state still refreshed — only the
        // navigation is suppressed. They were told their access would appear; yanking
        // them off the page they chose is not what they agreed to.
        if (isAbandoned()) return;
        navigate(route, { replace: true, flushSync: true });
      },
      refreshPurchaseState: () => store.mainAppStore.checkPurchaseState(),
      isSessionCurrent,
    });

    if (completion.refreshError) {
      // The code has already been consumed. A refresh failure must not retry
      // redemption and turn that success into an already-redeemed error.
      Sentry.captureException(completion.refreshError, {
        tags: { area: "org_access_code", phase: "refresh_after_success" },
      });
    }

    if (!completion.sessionCurrent) {
      onPresentationChange?.({ status: "idle" });
      return "session-changed" as const;
    }

    onPresentationChange?.({ status: "success", pending });
    return "success" as const;
  };

  let hadAmbiguousFailure = false;

  for (let attempt = 0; ; attempt += 1) {
    if (!isSessionCurrent()) {
      onPresentationChange?.({ status: "idle" });
      return "session-changed";
    }

    try {
      const outcome = await redeemStoredAccessCode(pending, store);

      if (!isSessionCurrent()) {
        onPresentationChange?.({ status: "idle" });
        return "session-changed";
      }

      if (outcome.ok === false) {
        if (
          isAmbiguousRedemptionRecoveryCandidate(
            outcome.reason,
            hadAmbiguousFailure,
          ) &&
          (await confirmCurrentUserReceivedOrgAccess(store))
        ) {
          if (!isSessionCurrent()) {
            onPresentationChange?.({ status: "idle" });
            return "session-changed";
          }

          return completeSuccessfulRedemption();
        }

        captureEvent("org_access_code_auto_redeem_fail", {
          exam_type: pending.examType,
          reason: outcome.reason,
          platform,
        });
        onPresentationChange?.({
          status: "error",
          pending,
          reason: outcome.reason,
        });
        return "typed-failure";
      }

      return completeSuccessfulRedemption();
    } catch (error) {
      if (!isSessionCurrent()) {
        onPresentationChange?.({ status: "idle" });
        return "session-changed";
      }

      hadAmbiguousFailure = true;
      if (await confirmCurrentUserReceivedOrgAccess(store)) {
        if (!isSessionCurrent()) {
          onPresentationChange?.({ status: "idle" });
          return "session-changed";
        }

        return completeSuccessfulRedemption();
      }

      const retryDelay = getAutoRedemptionRetryDelay(attempt);
      if (retryDelay !== null) {
        await delay(retryDelay);
        continue;
      }

      Sentry.captureException(error, {
        tags: { area: "org_access_code", phase: "auto_redeem" },
      });
      captureEvent("org_access_code_auto_redeem_fail", {
        exam_type: pending.examType,
        reason: "transport",
        platform,
      });
      if (isAbandoned()) {
        // They closed the dialog and were told to check their access, or re-enter the
        // code from their profile. Re-opening a modal over whatever they are doing now
        // would contradict that.
        onPresentationChange?.({ status: "idle" });
        return "transient-failure";
      }

      onPresentationChange?.({
        status: "error",
        pending,
        // A timeout is not the same as an unknown failure: the request may have been
        // completed by the server after we stopped waiting, so the student must not be
        // told it failed outright.
        reason: isRequestTimeoutError(error) ? "timed_out" : "unknown",
      });
      return "transient-failure";
    }
  }
}

export async function consumePendingAccessCode(
  opts: ConsumePendingAccessCodeOpts,
): Promise<ConsumePendingAccessCodeResult> {
  // A second link can arrive while the first request is in flight. Serialize
  // callers, then re-read storage so the newest pending record is not stranded.
  while (inFlight) {
    await inFlight;
  }

  const run = consumePendingAccessCodeInner(opts);
  inFlight = run;

  try {
    return await run;
  } finally {
    inFlight = null;
  }
}

export function usePendingAccessCode(
  {
    canAttempt,
    isMobileWeb,
    isAbandoned,
    onPresentationChange,
  }: {
    canAttempt: boolean;
    isMobileWeb: boolean;
    /** See ConsumePendingAccessCodeOpts.isAbandoned. */
    isAbandoned?: () => boolean;
    onPresentationChange?: (
      presentation: PendingAccessCodePresentation,
    ) => void;
  },
) {
  const { isSignedIn, userId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const store = useRootStore();
  const attemptedIdentityRef = useRef<string | null>(null);
  const currentUserIdRef = useRef(userId);
  const [retrySequence, setRetrySequence] = useState(0);
  currentUserIdRef.current = userId;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (location.pathname === "/redeem") {
      // RedeemOrgLinkPage must commit the current URL's code before the root
      // consumer reads storage. Resetting here also makes an explicit revisit
      // retry a transient failure with the same pending identity.
      attemptedIdentityRef.current = null;
      return;
    }

    if (
      !isSignedIn ||
      !userId ||
      !canAttempt ||
      params.has("afterSignIn") ||
      params.has("afterSignUp")
    ) {
      if (!isSignedIn) {
        attemptedIdentityRef.current = null;
      }
      return;
    }

    const pending = readPendingAccessCode();
    if (!pending) return;

    const identity = getPendingAccessCodeIdentity(pending);
    if (attemptedIdentityRef.current === identity) return;
    attemptedIdentityRef.current = identity;

    void consumePendingAccessCode({
      store,
      navigate,
      isMobileWeb,
      isSessionCurrent: () => currentUserIdRef.current === userId,
      isAbandoned,
      onPresentationChange,
    }).then((result) => {
      const currentPending = readPendingAccessCode();
      const currentIdentity = currentPending
        ? getPendingAccessCodeIdentity(currentPending)
        : null;
      const shouldTryAgain =
        result === "session-changed" ||
        (currentIdentity !== null && currentIdentity !== identity);

      if (shouldTryAgain) {
        attemptedIdentityRef.current = null;
        setRetrySequence((value) => value + 1);
      }
    });
  }, [
    canAttempt,
    isMobileWeb,
    isSignedIn,
    location.pathname,
    location.search,
    navigate,
    onPresentationChange,
    retrySequence,
    store,
    userId,
  ]);
}
